/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useForm, type Resolver } from 'react-hook-form';
import { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { SiPaypal } from '@icons-pack/react-simple-icons';
import { useRevalidator } from 'react-router';
import { ToggleCard, ToggleCardEdit, ToggleCardSummary } from '@/components/toggle-card';
import Badge from '@/components/badge';
import { Button } from '@/components/buttons';
import { Checkbox } from '@/components/forms/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/forms/radio-group';
import { Label } from '@/components/forms/label';
import { Form, FormControl, FormField, FormItem } from '@/components/forms/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, Check } from 'lucide-react';
import { VisaIcon, MastercardIcon, AmexIcon, DiscoverIcon } from '@/components/icons';
import { useBasket, useBasketUpdater } from '@/providers/basket';
import { createPaymentSchema, getPaymentDefaultValues, type PaymentData } from '@/lib/checkout/schemas';
import { getCardTypeDisplay, getLastFourDigits, PAYMENT_METHOD_IDS } from '@/lib/payment/payment-utils';
import { formatAddress, getAddressKey, isOrderBillingAddressIncomplete } from '@/lib/address/address-utils';
import { getCardIcon } from '@/lib/payment/card-icon-utils';
import { useCustomerProfile } from '@/hooks/checkout/use-customer-profile';
import { getAddressBookFromCustomer, getPaymentMethodsFromCustomer } from '@/lib/customer/profile-utils';
import { AddressFormFields } from '@/components/address-form-fields';
import { CreditCardInputFields } from '@/components/credit-card-input-fields';
import type { ShopperBasketsV2 } from '@/scapi';
import type { PaymentSubmissionRef } from '@/hooks/use-checkout-actions';
import type { CheckoutActionData } from '../types';
import CheckoutErrorBanner from './checkout-error-banner';
import { getCheckoutDisplayError } from '../utils/checkout-display-error';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import type { AcimaCheckoutConfig } from '@/lib/checkout/loaders.server';
import AcimaCheckoutButton from '@/components/acima/acima-checkout-button';
import { isAcimaFppZipRestricted } from '@/components/acima/fpp-zip-restriction';
import { Link } from '@/components/link';
import ConvergeLightBox from '@/components/converge-lightbox';
import type { ConvergeSitePrefs } from '@/lib/shopper-config-context';
import type { ContentAssetBody } from '@/lib/api/content-asset';
import type { ConvergePaymentToken } from '@/lib/elavon/types';
import PayPalButton, { type PayPalApprovedOrder } from '@/components/paypal/paypal-button';
import type { PayPalSdkConfig } from '@/lib/paypal/types';
import AshleyAdvantagePayment, {
    type SelectedFinancingTerm,
    type AshleyAdvantageHandle,
} from './ashley-advantage-payment';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { recomposeShopperVisibleTotal } from '@/lib/basket/recompose-total';
import { detectFinanceCardType } from '@/lib/payment/finance-card-type';
import { CheckoutContext } from '@/components/checkout/utils/checkout-context-types';

/** Accepted card-brand marks shown on the Credit Card option (matches AshCom Design System "Payment" panel). */
function CreditCardBrandLogos() {
    return (
        <span className="ml-auto flex shrink-0 items-center gap-1.5" aria-hidden>
            <MastercardIcon className="h-4 w-6" />
            <VisaIcon className="h-4 w-6" />
            <AmexIcon className="h-4 w-6" />
            <DiscoverIcon className="h-4 w-6" />
        </span>
    );
}

/** Acima wordmark shown on the lease-to-own option (no brand asset is bundled — styled text mark). */
function AcimaWordmark() {
    return (
        <span
            className="ml-auto shrink-0 text-base font-semibold lowercase tracking-tight text-brand-blue-700"
            aria-hidden>
            acima
        </span>
    );
}

/** Ashley Advantage® two-tone wordmark shown on the financing option (styled text mark). */
function AshleyAdvantageWordmark() {
    return (
        <span className="ml-auto shrink-0 text-right leading-none" aria-hidden>
            <span className="block text-xs font-bold uppercase tracking-wide text-foreground">Ashley</span>
            <span className="block text-xs font-semibold lowercase text-accent-secondary">advantage</span>
        </span>
    );
}

function PayPalBrandIcon() {
    return <SiPaypal className="ml-auto size-5 shrink-0 text-[#003087]" aria-hidden />;
}

/**
 * Map the Payment component's internal radio value (used for UI state — `'new'`, `'acima'`,
 * `'ashley-advantage'`, `'paypal'`, or a saved-payment-method id) to the equivalent SCAPI
 * `paymentMethodId` (`'CREDIT_CARD'`, `'ACIMA_FINANCE'`, `'ASHLEY_FINANCE'`, `'PayPal'`).
 *
 * Fed to `onPaymentMethodChange` so the parent (CheckoutFormPage) can drive optimistic UI
 * in sibling components (e.g. OrderSummary hiding the sales-tax row the moment Acima is
 * selected — before the server-side calc has zeroed tax on the basket) using the same
 * string constants that the OrderSummary already reads off `basket.paymentInstruments`.
 */
function radioValueToPaymentMethodId(radioValue: string): string {
    switch (radioValue) {
        case 'acima':
            return PAYMENT_METHOD_IDS.ACIMA_FINANCE;
        case 'ashley-advantage':
            return PAYMENT_METHOD_IDS.ASHLEY_FINANCE;
        case 'paypal':
            return PAYMENT_METHOD_IDS.PAYPAL;
        case '':
            return '';
        // 'new' AND any saved-payment-method id ("credit-card-…") both represent a card tender.
        default:
            return PAYMENT_METHOD_IDS.CREDIT_CARD;
    }
}

const REHYDRATE_RADIO_BY_PAYMENT_METHOD_ID: Record<string, 'paypal' | 'acima' | 'ashley-advantage'> = {
    [PAYMENT_METHOD_IDS.PAYPAL]: 'paypal',
    [PAYMENT_METHOD_IDS.ACIMA_FINANCE]: 'acima',
    [PAYMENT_METHOD_IDS.ASHLEY_FINANCE]: 'ashley-advantage',
};

interface PaymentProps {
    onSubmit: (data: PaymentData) => void;
    isLoading: boolean;
    actionData?: CheckoutActionData;
    // Step state managed by container
    isCompleted: boolean;
    isEditing: boolean;
    onEdit: () => void;
    disabled?: boolean;
    showBillingSameAsShipping?: boolean;
    /** Hide the "Save this payment to profile" checkbox even for logged-in customers. */
    hidePaymentSaveCheckbox?: boolean;
    paymentSubmissionRef?: PaymentSubmissionRef;
    /**
     * Notifies the parent whenever the shopper changes the payment-method radio.
     * Emits the SCAPI `paymentMethodId` equivalent (`'ACIMA_FINANCE'`, `'CREDIT_CARD'`, ...)
     * so the caller can drive optimistic UI without knowing the internal radio-value scheme.
     * Fired synchronously inside the change handler — no debounce, no basket round-trip.
     */
    onPaymentMethodChange?: (paymentMethodId: string) => void;
    /**
     * Non-sensitive Acima iframe config from loader. When `enabled` is true and the
     * basket/shipping address pass the price + restricted-state gates, the
     * ACIMA_FINANCE radio is shown alongside Credit Card. The lease iframe itself
     * is launched from the place-order step (AcimaCheckoutButton).
     */
    acimaConfig?: AcimaCheckoutConfig;
    convergePrefs?: ConvergeSitePrefs;
    /**
     * Mirrors the `enableSynchronyCVV` site preference. When true, the Ashley Advantage panel
     * reveals a security-code (CVV) field after a full 16-digit account number, requires it, and
     * the value is forwarded to the Synchrony authorization call (required on sandboxes with the
     * preference on — an empty value is rejected with 422 "SecurityCode is missing").
     */
    synchronyCvvEnabled?: boolean;
    /**
     * BM content asset body for the Ashley Advantage / Synchrony Terms Agreement legal copy,
     * forwarded into `<AshleyAdvantagePayment/>`. Streamed from the checkout loader (resolves
     * during checkout TTFB, but rendered behind a deep user-action gate so it is effectively
     * already resolved by the time it's shown). Empty body renders when the asset is missing.
     */
    synchronyTermsAgreementContentAsset?: Promise<ContentAssetBody | null>;
    /** DEV ONLY — prefill card fields for testing */
    devPrefillCard?: {
        number: string;
        holder: string;
        expMonth: string;
        expYear: string;
        cvv: string;
        type: string;
    };
}

export default function Payment({
    onSubmit,
    isLoading,
    actionData,
    isCompleted: _isCompleted,
    isEditing,
    onEdit,
    disabled = false,
    showBillingSameAsShipping = true,
    hidePaymentSaveCheckbox = false,
    paymentSubmissionRef,
    acimaConfig,
    convergePrefs,
    synchronyCvvEnabled = false,
    synchronyTermsAgreementContentAsset,
    devPrefillCard,
    onPaymentMethodChange,
}: PaymentProps) {
    const config = useConfig<AppConfig>();
    const revalidator = useRevalidator();
    // Read the checkout step machine directly (not via useCheckoutContext which throws): this
    // component also renders in Storybook / a couple of unit-test suites with no
    // <CheckoutProvider>, where the context is intentionally null. In real checkout it is always
    // present. We use it in handlePayPalApproved below to pin the shopper on the Payment step
    // once PayPal is attached, so returning customers (editingStep=null) do not get auto-advanced
    // past the "Continue" CTA by the basket-driven step effect.
    const checkout = useContext(CheckoutContext);
    const cart = useBasket();
    const updateBasket = useBasketUpdater();
    const customerProfile = useCustomerProfile();
    // Converge LightBox is always rendered on the frontend. The backend still owns the real
    // gate — the order.payment_instrument.afterPOST hook + site preferences decide whether the
    // Elavon authorization runs — so we don't depend on the convergePaymentEnabled flag here.
    const convergeEnabled = true;
    const [convergeSessionError, setConvergeSessionError] = useState<string | null>(null);
    const [convergeTokenCaptured, setConvergeTokenCaptured] = useState(false);
    // Display-only details of the card captured in the LightBox, so the collapsed/captured state can
    // show the brand + last four (matching the order-review summary) instead of generic text. The
    // PAN never reaches us — the masked number comes from Converge's hosted-callback response.
    const [capturedCard, setCapturedCard] = useState<{
        cardType: string;
        lastFour: string;
        expirationMonth?: number;
        expirationYear?: number;
    } | null>(null);
    // One-shot flag: when the shopper explicitly asks to (re)enter a card via "Use a different
    // card", the freshly-minted LightBox should open automatically rather than waiting for a second
    // click on the trigger button. Selecting Credit Card normally stays button-only.
    const [reopenLightBox, setReopenLightBox] = useState(false);
    const [convergeSession, setConvergeSession] = useState<{
        token: string;
        firstName: string;
        lastName: string;
        address: string;
        postalCode: string;
        txnId: string;
        totalAmount: number;
    } | null>(null);
    // 'new' or payment method ID. Use '' so we can detect "not yet initialized" and set preferred saved method once.
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
    const selectedPaymentMethodRef = useRef(selectedPaymentMethod);
    const userHasChosenPaymentMethodRef = useRef(false);
    selectedPaymentMethodRef.current = selectedPaymentMethod;

    // Acima lease-to-own result state. The iframe is launched from the AcimaCheckoutButton below;
    // on approval the lease is stored on the basket server-side and the shopper can place the order.
    const [acimaLeaseApproved, setAcimaLeaseApproved] = useState(false);
    const [acimaLeaseError, setAcimaLeaseError] = useState<string | null>(null);

    /** When false, show only first INITIAL_VISIBLE_COUNT options and "View all (n more)". See Figma NEXT Design System – Login/Checkout. */
    const [showAllPaymentOptions, setShowAllPaymentOptions] = useState(false);
    const [paypalInlineError, setPaypalInlineError] = useState<string | null>(null);
    // A failed payment response (`actionData`) is tied to the method that was submitted.
    // When the shopper switches to a different method without re-submitting, that error is
    // stale and must not keep rendering under the newly-selected method. Track the dismissed
    // response so we can suppress it until a fresh submit replaces `actionData`.
    const [dismissedPaymentErrorData, setDismissedPaymentErrorData] = useState<unknown>(null);
    const paymentSectionRef = useRef<HTMLDivElement | null>(null);
    const shouldScrollToPaymentOnCollapseRef = useRef(false);

    /** Track selected Ashley Advantage financing term — set after shopper accepts disclosures */
    const [ashleyAdvantageTerms, setAshleyAdvantageTerms] = useState<SelectedFinancingTerm | null>(null);
    /** Persist entered Ashley account number so "Edit" restores the field value. */
    const [lastAshleyAccountNumber, setLastAshleyAccountNumber] = useState('');
    const [showAshleyTermsOnly, setShowAshleyTermsOnly] = useState(false);
    /** Imperative handle to trigger the Ashley Advantage financing-offers lookup from the footer button. */
    const ashleyAdvantageRef = useRef<AshleyAdvantageHandle | null>(null);

    const { t } = useTranslation('checkout');
    const ta = t as (key: string, options?: Record<string, unknown>) => string;
    const isUpcomingStep = disabled && !isEditing;

    const INITIAL_VISIBLE_COUNT = 3;
    const paymentFormError =
        actionData && actionData === dismissedPaymentErrorData
            ? undefined
            : getCheckoutDisplayError(actionData, 'payment');

    // Get customer's saved payment methods. Memoize so the array reference is stable across renders —
    // the helpers build a new array every call, which would otherwise re-run the bootstrap effect below
    // on every render and clobber the user's selection (e.g. resetting Acima/Ashley Advantage back to 'new').
    const savedPaymentMethods = useMemo(() => getPaymentMethodsFromCustomer(customerProfile), [customerProfile]);
    const savedAddresses = useMemo(() => getAddressBookFromCustomer(customerProfile), [customerProfile]);

    // Set default payment method only on mount or when savedPaymentMethods change. Use a ref to read
    // current selection so we don't depend on selectedPaymentMethod and avoid an effect loop.
    useEffect(() => {
        if (savedPaymentMethods.length === 0) {
            // No saved methods: leave the selection empty so the shopper must actively pick a
            // payment method (Credit Card / Acima / Ashley Advantage). Auto-selecting Credit Card
            // would pre-trigger the Converge session + lightbox before the shopper opts into card
            // payment.
            return;
        }

        const current = selectedPaymentMethodRef.current;

        // Never clobber a tender the shopper actively chose. This is critical for the non-card
        // methods ('acima', 'ashley-advantage') whose ids are NOT in the saved-method set: a
        // loader revalidation (e.g. after the Acima lease POST or a failed place-order auth)
        // recomputes savedPaymentMethods, re-runs this effect, sees the id as "invalid below",
        // and would otherwise silently reset the selection back to a saved card / 'new'. The
        // next place-order then submits empty hidden card fields and the server rejects with
        // credit-card validation errors instead of the real (non-card) outcome.
        if (userHasChosenPaymentMethodRef.current && current !== '') {
            return;
        }

        const validIds = new Set(savedPaymentMethods.map((m) => m.id));
        const isValidSelection = current === 'new' || validIds.has(current);
        const shouldReplaceBootstrapNewSelection =
            current === 'new' && savedPaymentMethods.length > 0 && !userHasChosenPaymentMethodRef.current;
        if (isValidSelection && current !== '' && !shouldReplaceBootstrapNewSelection) {
            return; // Preserve user's choice
        }

        const preferredWithId = savedPaymentMethods.find((method) => method.preferred && method.id);
        const firstWithId = savedPaymentMethods.find((method) => method.id);
        setSelectedPaymentMethod(preferredWithId?.id ?? firstWithId?.id ?? 'new');
    }, [savedPaymentMethods]);

    const shippingAddress = cart?.shipments?.[0]?.shippingAddress;
    const paymentMethod = cart?.paymentInstruments?.[0];
    const billingAddress = cart?.billingAddress;

    // SSF-557 — track the previous basket PI so we can detect a PI removal driven
    // by the server (the `?entry=cart` / `?entry=minicart` CC clear in
    // `src/lib/checkout/loaders.server.ts#clearPaymentDetailsOnCheckoutEntry`, or
    // the amount-mismatch invalidator in
    // `cartridges/sfnext_ashley_hooks/.../PaymentInstrumentInvalidator.js`) and
    // reset the tender-specific captured client state. Without this, the client-side
    // `BasketProvider` gets updated to the post-clear basket a beat AFTER the
    // Payment component has already latched the tender's local state off the
    // stale pre-clear basket in the useEffect below — and there is no other code
    // path that resets those fields when the PI disappears from the basket
    // (`handleReenterCard` / the "Edit selected term" button require an explicit
    // user click; the tier-switch reset at line ~921 only fires on
    // `paymentRadioValue` transitions, not on external PI removal). Result: the
    // Payment summary stays visually attached to the just-removed tender until
    // the shopper hard-refreshes, breaking every AC of SSF-557:
    //   • AC1 (CC, `?entry=cart` clear) — QA repro on bmkt004 (2026-08-10).
    //   • AC2 (Non-CC + cart-total-change invalidation via SCAPI hook) — this
    //     bites in the mini-cart-on-checkout path where the shopper opens the
    //     mini-cart sheet from /checkout, bumps qty, and returns to the same
    //     still-mounted /checkout tree (fresh mount would default-init the
    //     state, but the sheet path keeps the component mounted).
    const previousPaymentMethodIdRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        const previousPaymentMethodId = previousPaymentMethodIdRef.current;
        previousPaymentMethodIdRef.current = paymentMethod?.paymentMethodId ?? undefined;

        if (!paymentMethod) {
            // Basket PI just disappeared. Reset the tender-specific captured
            // client state for whichever tender just detached, so the shopper
            // sees the empty payment step and must re-authorize. Guarded by
            // "previous was <tender>" so a transient pre-attach capture (e.g.
            // Converge LightBox sets `convergeTokenCaptured` + `capturedCard`
            // BEFORE the CREDIT_CARD PI attaches; the Ashley Advantage panel
            // sets `ashleyAdvantageTerms` BEFORE the ASHLEY_FINANCE PI
            // attaches) is never clobbered.
            //
            // The `userHasChosenPaymentMethodRef` + `setSelectedPaymentMethod('')`
            // reset is common to all three tenders — the previous PI attach set
            // the ref implicitly, and leaving it `true` would suppress the
            // savedPaymentMethods bootstrap effect on future logged-in sessions
            // and jam the Payment step in a permanent no-selection state.
            if (previousPaymentMethodId === PAYMENT_METHOD_IDS.CREDIT_CARD) {
                setConvergeTokenCaptured(false);
                setCapturedCard(null);
                userHasChosenPaymentMethodRef.current = false;
                setSelectedPaymentMethod('');
            } else if (previousPaymentMethodId === PAYMENT_METHOD_IDS.ASHLEY_FINANCE) {
                // SSF-557 AC2 — Ashley Advantage (Synchrony) latch:
                // `ashleyAdvantageTerms` gates the "confirmed term" summary
                // card (`hasAshleyAdvantageSummary` at ~L467) and the
                // Continue-to-review CTA (`isPaymentReadyToAuth` at ~L494).
                // Both stayed `true` on qty-change-driven PI invalidation,
                // rendering the Payment step as if the PI were still attached.
                setAshleyAdvantageTerms(null);
                userHasChosenPaymentMethodRef.current = false;
                setSelectedPaymentMethod('');
            } else if (previousPaymentMethodId === PAYMENT_METHOD_IDS.ACIMA_FINANCE) {
                // SSF-557 AC2 — Acima Finance latch: `acimaLeaseApproved`
                // gates the Continue-to-review CTA (`isPaymentReadyToAuth` at
                // ~L490) and hides the "Sign Lease" iframe (`!acimaLeaseApproved`
                // at ~L1293). Same failure mode as Ashley Advantage — the
                // approved-lease state survives the server-side PI clear.
                setAcimaLeaseApproved(false);
                setAcimaLeaseError(null);
                userHasChosenPaymentMethodRef.current = false;
                setSelectedPaymentMethod('');
            }
            // PayPal deliberately does NOT have a branch here: the "PayPal
            // attached" signal (`basketHasPayPal` at ~L438) is derived from
            // `cart.paymentInstruments` — when the basket PI is dropped the
            // derivation flips to `false` on the same render, no local latch
            // to reset. `paypalInlineError` is a transient error state that
            // clears on the next PayPal SDK interaction.
            return;
        }
        const currentSelection = selectedPaymentMethodRef.current;
        if (userHasChosenPaymentMethodRef.current) {
            return;
        }
        const mappedRadioValue = REHYDRATE_RADIO_BY_PAYMENT_METHOD_ID[paymentMethod.paymentMethodId ?? ''];
        if (mappedRadioValue) {
            if (currentSelection === mappedRadioValue) {
                return;
            }
            setSelectedPaymentMethod(mappedRadioValue);
            return;
        }

        if (paymentMethod.paymentMethodId !== PAYMENT_METHOD_IDS.CREDIT_CARD) {
            return;
        }

        const instrumentCardType = paymentMethod.paymentCard?.cardType;
        const instrumentLastFour = getLastFourDigits(
            paymentMethod.paymentCard?.numberLastDigits ?? paymentMethod.paymentCard?.maskedNumber
        );
        const savedMatch = savedPaymentMethods.find((method) => {
            const methodLastFour = getLastFourDigits(method.maskedNumber);
            const matchesLastFour = Boolean(
                methodLastFour && instrumentLastFour && methodLastFour === instrumentLastFour
            );
            const matchesCardType = (method.cardType ?? '') === (instrumentCardType ?? '');
            return matchesLastFour && matchesCardType;
        });

        if (savedMatch?.id) {
            setSelectedPaymentMethod(savedMatch.id);
            return;
        }

        setSelectedPaymentMethod('new');
        setConvergeTokenCaptured(true);
        setCapturedCard({
            cardType: instrumentCardType ?? '',
            lastFour: instrumentLastFour,
            expirationMonth: paymentMethod.paymentCard?.expirationMonth,
            expirationYear: paymentMethod.paymentCard?.expirationYear,
        });
    }, [paymentMethod, savedPaymentMethods]);

    // Resolved value for RadioGroup. Logged-in shoppers default to their preferred/first saved
    // method; shoppers with no saved methods start with no selection ('') so Credit Card is not
    // auto-selected — they must actively choose a payment method.
    const paymentRadioValue =
        selectedPaymentMethod ||
        (savedPaymentMethods.length > 0
            ? (savedPaymentMethods.find((m) => m.preferred)?.id ?? savedPaymentMethods[0]?.id ?? 'new')
            : '');

    const selectedSavedMethod =
        paymentRadioValue !== 'new' ? savedPaymentMethods.find((method) => method.id === paymentRadioValue) : undefined;
    const hasSummaryPaymentMethod = Boolean(paymentMethod || selectedSavedMethod);
    const summaryMethodLabel = paymentMethod
        ? getCardTypeDisplay(paymentMethod)
        : selectedSavedMethod
          ? getCardTypeDisplay({
                paymentCard: { cardType: selectedSavedMethod.cardType },
            } as ShopperBasketsV2.schemas['OrderPaymentInstrument'])
          : '';
    const basketHasPayPal = (cart?.paymentInstruments ?? []).some(
        (instrument) => instrument.paymentMethodId === PAYMENT_METHOD_IDS.PAYPAL
    );
    const summaryLastFour =
        getLastFourDigits(paymentMethod?.paymentCard?.numberLastDigits || paymentMethod?.paymentCard?.maskedNumber) ||
        getLastFourDigits(selectedSavedMethod?.maskedNumber);
    const summaryFinanceInstrument = paymentMethod as
        | (ShopperBasketsV2.schemas['OrderPaymentInstrument'] & {
              c_financeCardNumber?: string;
              c_financeCardType?: string;
          })
        | undefined;
    const summaryFinanceLastFour =
        paymentMethod?.paymentMethodId === PAYMENT_METHOD_IDS.ASHLEY_FINANCE
            ? getLastFourDigits(summaryFinanceInstrument?.c_financeCardNumber)
            : '';
    const summaryFinanceLabel =
        paymentMethod?.paymentMethodId === PAYMENT_METHOD_IDS.ASHLEY_FINANCE
            ? summaryFinanceInstrument?.c_financeCardType || summaryMethodLabel || 'Ashley Advantage'
            : summaryMethodLabel;
    const summaryExpiryMonthRaw = paymentMethod?.paymentCard?.expirationMonth ?? selectedSavedMethod?.expirationMonth;
    const summaryExpiryYearRaw = paymentMethod?.paymentCard?.expirationYear ?? selectedSavedMethod?.expirationYear;
    const summaryExpiryMonth =
        summaryExpiryMonthRaw !== undefined && summaryExpiryMonthRaw !== null
            ? String(summaryExpiryMonthRaw).padStart(2, '0')
            : '';
    const summaryExpiryYear =
        summaryExpiryYearRaw !== undefined && summaryExpiryYearRaw !== null ? String(summaryExpiryYearRaw) : '';
    const hasSummaryExpiry = Boolean(summaryExpiryMonth && summaryExpiryYear);
    const hasAshleyAdvantageSummary = paymentRadioValue === 'ashley-advantage' && Boolean(ashleyAdvantageTerms);
    const ashleyAccountLastFour = getLastFourDigits(ashleyAdvantageTerms?.accountNumber);
    const ashleyCardProviderLabel = detectFinanceCardType(ashleyAdvantageTerms?.accountNumber ?? '');
    const shopperVisibleTotal = recomposeShopperVisibleTotal(cart);
    const ashleyFinancedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(shopperVisibleTotal);

    // "Continue to review" must stay hidden until the selected tender actually has a payment
    // instrument that is ready to authorize — i.e. the shopper has entered/captured the payment
    // details, not merely highlighted the radio. Each tender exposes that capture through its own
    // client signal:
    //   - 'new' (Credit Card): the Converge LightBox token has been captured
    //   - saved card: a saved method is selected
    //   - 'acima': the lease was approved via the Acima iframe
    //   - 'ashley-advantage': a financing term was accepted in the AA panel
    // When nothing is selected ('') every branch is false, so the button stays hidden.
    const isPaymentReadyToAuth =
        paymentRadioValue === 'new'
            ? convergeTokenCaptured
            : paymentRadioValue === 'acima'
              ? acimaLeaseApproved
              : paymentRadioValue === 'paypal'
                ? basketHasPayPal
                : paymentRadioValue === 'ashley-advantage'
                  ? Boolean(ashleyAdvantageTerms)
                  : Boolean(selectedSavedMethod);

    /**
     * Acima eligibility — mirrors legacy SFRA STATE_ELIGIBLE_FOR_ACIMA. The Acima enabled
     * flag and restricted-state list come from the loader (config.acima.*).
     *
     * There is intentionally **no** min-price or max-total gate at checkout. Acima's own
     * iframe surfaces the real minimum/maximum if a shopper somehow reaches it with an
     * out-of-range total — matching the legacy behavior of always exposing the option
     * (state restriction aside) and letting Acima itself enforce merchant-level thresholds.
     * The legacy `acimaFinanceMinimumPriceThresholdForPDP` value is PDP-only messaging and
     * is not read at checkout.
     */
    const isAcimaEligible = useMemo(() => {
        if (!acimaConfig?.enabled) return false;
        const stateCode = shippingAddress?.stateCode?.toUpperCase();
        if (stateCode && acimaConfig.restrictedStates?.some((s) => s.toUpperCase() === stateCode)) {
            return false;
        }
        return true;
    }, [acimaConfig, shippingAddress?.stateCode]);

    /**
     * Acima FPP-in-restricted-ZIP block — legacy parity for the
     * `handyFppRestrictionsInStatesWithAcimaPayment` site preference.
     *
     * True when BOTH conditions hold:
     *   1. The basket contains at least one Furniture Protection Plan
     *      (`c_isProtectionPlan` stamped per-item by `BasketEnrichmentHelper`).
     *   2. The shipping ZIP falls in one of the configured 3-digit prefix ranges
     *      (`acima.fppRestrictedZipRanges`, default `460-479 / 900-961 / 100-149`).
     *
     * In this state the Acima radio still renders (so the shopper can discover
     * WHY it's unavailable), but the expanded content swaps the Sign Lease iframe
     * for an inline warning telling them to remove the FPP or pick another method.
     * The `getCheckoutDetails` SCAPI endpoint also rejects with fault
     * `acima-fpp-restricted-zip` as defense-in-depth against stale clients.
     */
    const isAcimaFppZipBlocked = useMemo(() => {
        if (!acimaConfig?.enabled) return false;
        const hasFpp = (cart?.productItems ?? []).some(
            (item) => (item as { c_isProtectionPlan?: boolean }).c_isProtectionPlan === true
        );
        return isAcimaFppZipRestricted(hasFpp, shippingAddress?.postalCode, acimaConfig.fppRestrictedZipPrefixes ?? []);
    }, [acimaConfig, cart?.productItems, shippingAddress?.postalCode]);

    // SSF-400: Pay Later visibility in the checkout Payment section reads the
    // server-stamped `c_payPalPayLaterEligible` boolean on the basket (see
    // `int_sfnext_paypal/scripts/paypal/PayLater.js`). Same pattern as
    // `cart-express-payments.tsx` / `checkout-express-payments.tsx` — env-level
    // `payLater.enabled` gates SDK funding-source loading; the per-basket flag
    // gates the visible button slot.
    const payLaterEnvEnabled = config?.payment?.paypal?.payLater?.enabled ?? false;
    const payLaterEligibleForBasket = Boolean(
        (cart as { c_payPalPayLaterEligible?: boolean } | undefined)?.c_payPalPayLaterEligible
    );
    const showPayLaterButton = payLaterEnvEnabled && payLaterEligibleForBasket;

    const paypalSdkConfig = useMemo<PayPalSdkConfig | null>(() => {
        const paypal = config?.payment?.paypal;
        if (!paypal?.clientId) return null;
        return {
            clientId: paypal.clientId,
            sdkBaseUrl: paypal.sdkBaseUrl,
            // Keep SDK currency aligned with the active basket currency so PayPal popup
            // shows the same currency/amount context as checkout totals.
            currency: cart?.currency ?? paypal.currency,
            intent: paypal.intent,
            enableFunding: showPayLaterButton ? 'paylater' : undefined,
        };
    }, [config?.payment?.paypal, showPayLaterButton, cart?.currency]);

    const allPaymentOptionIds = useMemo(
        () => [
            ...savedPaymentMethods.map((m) => m.id),
            'new',
            ...(paypalSdkConfig ? ['paypal'] : []),
            ...(isAcimaEligible ? ['acima'] : []),
        ],
        [savedPaymentMethods, paypalSdkConfig, isAcimaEligible]
    );
    const visiblePaymentOptionIds = useMemo(() => {
        if (showAllPaymentOptions || allPaymentOptionIds.length <= INITIAL_VISIBLE_COUNT) {
            return allPaymentOptionIds;
        }
        const first = allPaymentOptionIds.slice(0, INITIAL_VISIBLE_COUNT);
        const current = paymentRadioValue;
        if (!first.includes(current)) {
            return [...first, current];
        }
        return first;
    }, [showAllPaymentOptions, allPaymentOptionIds, paymentRadioValue]);
    const hiddenPaymentCount = allPaymentOptionIds.length - visiblePaymentOptionIds.length;
    const showViewLessUnderForm =
        savedPaymentMethods.length > 0 &&
        allPaymentOptionIds.length > INITIAL_VISIBLE_COUNT &&
        hiddenPaymentCount === 0;
    const handleViewLess = () => {
        setShowAllPaymentOptions(false);
        shouldScrollToPaymentOnCollapseRef.current = true;
        const firstVisible = allPaymentOptionIds.slice(0, INITIAL_VISIBLE_COUNT);
        if (!firstVisible.includes(paymentRadioValue)) {
            userHasChosenPaymentMethodRef.current = true;
            setSelectedPaymentMethod(firstVisible[0]);
        }
    };

    const handlePaymentMethodSelectionChange = (value: string) => {
        userHasChosenPaymentMethodRef.current = true;
        setPaypalInlineError(null);
        setSelectedPaymentMethod(value);
    };

    // Optimistic UI notification — fire `onPaymentMethodChange` on every effective change of
    // the selected tender so sibling components (OrderSummary) can react immediately, before
    // the payment step is submitted and the basket recalculates on the server.
    //
    // Kept as an effect (rather than firing directly in `handlePaymentMethodSelectionChange`)
    // because there are MULTIPLE call sites that mutate `selectedPaymentMethod`:
    //   - the main saved-method RadioGroup uses `handlePaymentMethodSelectionChange`
    //   - each individual sub-option ("Credit Card" compact, Ashley Advantage, Acima, PayPal)
    //     has its own inline `onValueChange` that calls `setSelectedPaymentMethod` directly
    //   - `handleViewLess` also resets it when the current selection is hidden
    //   - the bootstrap effect above seeds a preferred saved method on mount
    // Watching `paymentRadioValue` (the resolved radio value, which folds the saved-method
    // fallback into `selectedPaymentMethod === ''`) guarantees the parent stays in sync with
    // what the RadioGroup is actually rendering as `value`, regardless of which handler
    // triggered the update. Firing on the resolved value also means the first render after
    // mount notifies the parent of the initial selection (usually a saved card → 'CREDIT_CARD').
    useEffect(() => {
        if (!onPaymentMethodChange) return;
        onPaymentMethodChange(radioValueToPaymentMethodId(paymentRadioValue));
    }, [paymentRadioValue, onPaymentMethodChange]);

    // Dismiss a stale payment error when the shopper switches to a different tender. The error
    // belongs to the previously-submitted method; a new submit will produce a fresh `actionData`
    // (new object reference) which re-arms the banner. Only fires on an actual change of the
    // resolved radio value — not on mount — so a freshly-received error is never hidden.
    const prevPaymentRadioValueRef = useRef(paymentRadioValue);
    useEffect(() => {
        if (prevPaymentRadioValueRef.current === paymentRadioValue) return;
        prevPaymentRadioValueRef.current = paymentRadioValue;
        if (actionData) {
            setDismissedPaymentErrorData(actionData);
        }
    }, [paymentRadioValue, actionData]);

    useEffect(() => {
        if (!showAllPaymentOptions && shouldScrollToPaymentOnCollapseRef.current) {
            paymentSectionRef.current?.scrollIntoView({ block: 'start' });
            shouldScrollToPaymentOnCollapseRef.current = false;
        }
    }, [showAllPaymentOptions]);

    // Helper function to check if billing address is same as shipping address
    const isBillingSameAsShipping = (
        billingAddr:
            | {
                  firstName?: string;
                  lastName?: string;
                  address1?: string;
                  city?: string;
                  stateCode?: string;
                  postalCode?: string;
              }
            | undefined,
        shippingAddr:
            | {
                  firstName?: string;
                  lastName?: string;
                  address1?: string;
                  city?: string;
                  stateCode?: string;
                  postalCode?: string;
              }
            | undefined
    ): boolean => {
        if (!billingAddr || !shippingAddr) return false;

        return (
            billingAddr.firstName === shippingAddr.firstName &&
            billingAddr.lastName === shippingAddr.lastName &&
            billingAddr.address1 === shippingAddr.address1 &&
            billingAddr.city === shippingAddr.city &&
            billingAddr.stateCode === shippingAddr.stateCode &&
            billingAddr.postalCode === shippingAddr.postalCode
        );
    };

    const billingAddressOptions = useMemo(
        () => savedAddresses.filter((addr) => !isBillingSameAsShipping(addr, shippingAddress)),
        [savedAddresses, shippingAddress]
    );

    // Memoize default values to prevent infinite re-renders
    // Only use basket payment instrument holder when user has selected that saved method.
    const defaultValues = useMemo(() => {
        const baseDefaults = getPaymentDefaultValues({
            shippingAddress,
            paymentMethod:
                paymentRadioValue !== 'new' && paymentMethod?.paymentCard?.holder
                    ? { holder: paymentMethod.paymentCard.holder }
                    : undefined,
        });

        const isUsingSavedPayment = savedPaymentMethods.some((m) => m.id === paymentRadioValue);

        // Default "same as shipping" ON for delivery checkout, but OFF when the basket already has a complete
        // billing address that differs from shipping — avoids submitting with true and overwriting that address.
        const basketHasDistinctBilling = Boolean(
            showBillingSameAsShipping &&
                billingAddress &&
                !isOrderBillingAddressIncomplete(billingAddress) &&
                shippingAddress &&
                !isBillingSameAsShipping(billingAddress, shippingAddress)
        );
        const defaultBillingSameAsShipping = showBillingSameAsShipping ? !basketHasDistinctBilling : false;

        const computedDefaults = {
            ...baseDefaults,
            // Do not pre-fill cardholder name; user enters it when adding a new card.
            cardholderName: '',
            useSavedPaymentMethod: isUsingSavedPayment,
            selectedSavedPaymentMethod: isUsingSavedPayment ? paymentRadioValue : undefined,
            billingSameAsShipping: defaultBillingSameAsShipping,
            ...(basketHasDistinctBilling && billingAddress
                ? {
                      billingFirstName: billingAddress.firstName ?? '',
                      billingLastName: billingAddress.lastName ?? '',
                      billingAddress1: billingAddress.address1 ?? '',
                      billingAddress2: billingAddress.address2 ?? '',
                      billingCity: billingAddress.city ?? '',
                      billingStateCode: billingAddress.stateCode ?? '',
                      billingPostalCode: billingAddress.postalCode ?? '',
                      billingCountryCode: billingAddress.countryCode ?? 'US',
                  }
                : {}),
            // @sfdc-extension-block-start SFDC_EXT_BOPIS
            // For BOPIS orders, don't pre-fill billing address or cardholder name from shipping (which is the store address)
            ...(!showBillingSameAsShipping && {
                cardholderName: '',
                billingFirstName: '',
                billingLastName: '',
                billingAddress1: '',
                billingAddress2: '',
                billingCity: '',
                billingStateCode: '',
                billingPostalCode: '',
                billingCountryCode: 'US',
            }),
            // @sfdc-extension-block-end SFDC_EXT_BOPIS
        };

        return computedDefaults;
    }, [
        paymentRadioValue,
        shippingAddress,
        paymentMethod,
        savedPaymentMethods,
        showBillingSameAsShipping,
        billingAddress,
    ]);

    // Checkout payment step: credit card is captured in the Converge LightBox and alternative
    // tenders (PayPal, Acima, Ashley Advantage) are attached out-of-band, so "Continue to review"
    // must only validate the billing address (when it differs from shipping) — never inline card
    // fields. Otherwise the empty card form fails validation and the Continue button silently
    // no-ops for any non-inline-card tender.
    const schema = useMemo(
        () => createPaymentSchema(t as (key: string) => string, { skipCardFieldValidation: true }),
        [t]
    );

    // `createPaymentSchema` is annotated `z.ZodType<PaymentData, unknown>` to break a
    // type-instantiation explosion (see schemas.ts PaymentData docs). zodResolver constrains the
    // schema's input to `FieldValues`, which `unknown` does not satisfy, so we pin the resolver
    // result to `Resolver<PaymentData>`. Runtime behavior is unchanged — the resolver still
    // validates against the exact same schema instance.
    const resolver = useMemo<Resolver<PaymentData>>(
        () => zodResolver(schema as Parameters<typeof zodResolver>[0]) as unknown as Resolver<PaymentData>,
        [schema]
    );

    const form = useForm<PaymentData>({
        resolver,
        defaultValues,
        mode: 'onSubmit', // Only validate on submit, not on change/blur
    });

    // Mints a Converge LightBox session token via the Custom SCAPI endpoint
    // (`/action/get-payment-session`), which reads PCI-sensitive credentials server-side — the
    // browser only ever sees an opaque, single-use token. Sets the session in place (no null
    // first) so callers can refresh the token without unmounting the LightBox.
    const loadConvergeSession = useCallback(() => {
        const billing = cart?.billingAddress;
        const shipping = cart?.shipments?.[0]?.shippingAddress;
        const addr = billing ?? shipping;

        fetch('/action/get-payment-session', { method: 'POST' })
            .then(
                (res) =>
                    res.json() as Promise<{
                        success: boolean;
                        token?: string;
                        firstName?: string;
                        lastName?: string;
                        address?: string;
                        postalCode?: string;
                        txnId?: string;
                        totalAmount?: number;
                        error?: string;
                    }>
            )
            .then((data) => {
                if (!data.success || !data.token) {
                    setConvergeSessionError(data.error ?? 'Failed to load payment form.');
                    return;
                }
                setConvergeSession({
                    token: data.token,
                    firstName: data.firstName ?? addr?.firstName ?? '',
                    lastName: data.lastName ?? addr?.lastName ?? '',
                    address: data.address ?? addr?.address1 ?? '',
                    postalCode: data.postalCode ?? addr?.postalCode ?? '',
                    txnId: data.txnId ?? '',
                    totalAmount: data.totalAmount ?? 0,
                });
            })
            .catch(() => setConvergeSessionError('Unable to initialise payment. Please try again.'));
    }, [cart]);

    // Fetch the session token only once Credit Card is the selected payment method. Fetching on
    // payment-step entry — before the shopper has chosen to pay by card — wastes the single-use
    // token (e.g. when a saved card, Acima, or Ashley Advantage is active, or nothing is selected).
    // The `convergeSession` guard short-circuits when a token is already held, so this never
    // refetches while one is in hand (no loop).
    useEffect(() => {
        if (!convergeEnabled || !isEditing || convergeSession || paymentRadioValue !== 'new') return;
        loadConvergeSession();
    }, [convergeEnabled, isEditing, paymentRadioValue, convergeSession, loadConvergeSession]);

    const handleConvergeToken = useCallback(
        (tokenData: ConvergePaymentToken) => {
            form.setValue('isLightBoxPayment', true);
            form.setValue('sslToken', tokenData.sslToken);
            form.setValue('convergeCardType', tokenData.cardType);
            form.setValue('convergeCardHolder', tokenData.cardHolder);
            form.setValue('convergeExpirationMonth', tokenData.expirationMonth);
            form.setValue('convergeExpirationYear', tokenData.expirationYear);
            // Converge returns the masked PAN as ssl_card_number in the hosted-callback response.
            form.setValue('convergeMaskedNumber', tokenData.transactionInfo?.ssl_card_number ?? '');
            // Session token + full transactioninfo blob — piped to the SCAPI payment-instrument
            // so ElavonAuthorizationHelper can persist them as legacy-parity order notes
            // (`Converge Session Token`, `Checkout JS Transaction`) at auth time and then null
            // the attributes. Same short-lived-attr pattern as `sslToken`.
            form.setValue('convergeSessionToken', convergeSession?.token ?? '');
            form.setValue('convergeTransactionInfo', JSON.stringify(tokenData.transactionInfo));
            setCapturedCard({
                cardType: tokenData.cardType,
                lastFour: getLastFourDigits(tokenData.transactionInfo?.ssl_card_number),
                expirationMonth: tokenData.expirationMonth,
                expirationYear: tokenData.expirationYear,
            });
            setConvergeTokenCaptured(true);
            setConvergeSessionError(null);
        },
        [form, convergeSession]
    );

    // "Use a different card" from the captured state. The Converge session token is single-use
    // (consumed when the card was captured / on a declined place-order), so we discard it AND the
    // captured token fields; nulling convergeSession re-triggers the session-fetch effect to mint a
    // fresh token and re-render the LightBox trigger button. Mirrors the tier-switch reset for the
    // 'new' tender.
    const handleReenterCard = useCallback(() => {
        setConvergeTokenCaptured(false);
        setCapturedCard(null);
        setConvergeSession(null);
        setConvergeSessionError(null);
        // Explicit shopper intent to enter another card — auto-open the next minted LightBox.
        setReopenLightBox(true);
        form.setValue('isLightBoxPayment', false);
        form.setValue('sslToken', '');
        form.setValue('convergeCardType', '');
        form.setValue('convergeCardHolder', '');
        form.setValue('convergeExpirationMonth', undefined);
        form.setValue('convergeExpirationYear', undefined);
        form.setValue('convergeMaskedNumber', '');
    }, [form]);

    // DEV ONLY — populate card fields when devPrefillCard prop is set (skipped when Converge LightBox is enabled).
    useEffect(() => {
        if (!devPrefillCard || convergeEnabled) return;
        form.setValue('cardNumber', devPrefillCard.number);
        form.setValue('cardholderName', devPrefillCard.holder);
        form.setValue('expiryDate', `${devPrefillCard.expMonth}/${devPrefillCard.expYear}`);
        form.setValue('cvv', devPrefillCard.cvv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [devPrefillCard]);

    const shippingAddressSyncKey = useMemo(
        () => (shippingAddress ? getAddressKey(shippingAddress) : ''),
        [shippingAddress]
    );
    const previousBillingSameAsShippingRef = useRef<boolean | null>(null);

    // Keep billing fields aligned with cart shipping while "same as shipping" is checked (e.g. after OTP / loader prefill).
    const billingSameAsShippingWatched = form.watch('billingSameAsShipping');
    useEffect(() => {
        if (!showBillingSameAsShipping || !shippingAddress || !shippingAddressSyncKey) return;

        const previousValue = previousBillingSameAsShippingRef.current;
        const toggledFromSameToDifferent = previousValue === true && billingSameAsShippingWatched === false;
        previousBillingSameAsShippingRef.current = billingSameAsShippingWatched;

        if (billingSameAsShippingWatched) {
            form.setValue('billingFirstName', shippingAddress.firstName ?? '');
            form.setValue('billingLastName', shippingAddress.lastName ?? '');
            form.setValue('billingAddress1', shippingAddress.address1 ?? '');
            form.setValue('billingAddress2', shippingAddress.address2 ?? '');
            form.setValue('billingCity', shippingAddress.city ?? '');
            form.setValue('billingStateCode', shippingAddress.stateCode ?? '');
            form.setValue('billingPostalCode', shippingAddress.postalCode ?? '');
            form.setValue('billingCountryCode', shippingAddress.countryCode ?? 'US');
            return;
        }

        if (toggledFromSameToDifferent) {
            form.setValue('billingFirstName', '');
            form.setValue('billingLastName', '');
            form.setValue('billingAddress1', '');
            form.setValue('billingAddress2', '');
            form.setValue('billingCity', '');
            form.setValue('billingStateCode', '');
            form.setValue('billingPostalCode', '');
            form.setValue('billingCountryCode', 'US');
        }
    }, [showBillingSameAsShipping, shippingAddress, shippingAddressSyncKey, billingSameAsShippingWatched, form]);

    // Update form values when selected payment method changes
    useEffect(() => {
        const effectiveSelection = selectedPaymentMethod || paymentRadioValue;
        const isAcimaSelected = effectiveSelection === 'acima';
        const isUsingSavedPayment = savedPaymentMethods.some((m) => m.id === effectiveSelection);

        form.setValue('useSavedPaymentMethod', isUsingSavedPayment);
        form.setValue('selectedSavedPaymentMethod', isUsingSavedPayment ? effectiveSelection : undefined);
        form.setValue('paymentMethodId', isAcimaSelected ? PAYMENT_METHOD_IDS.ACIMA_FINANCE : undefined);

        if (isUsingSavedPayment || isAcimaSelected) {
            void form.trigger();
        }
    }, [selectedPaymentMethod, paymentRadioValue, savedPaymentMethods, form]);

    // Reset the abandoned tender's captured client state whenever the shopper switches tiers, so a
    // stale capture from the previous tier can never be re-submitted and the UI is clean on return.
    // Mirrors legacy `COBilling.resetPaymentForms` clearing the non-selected methods' form elements:
    //   - leaving Credit Card → discard the single-use Converge session/token (returning forces a
    //     fresh one via the session-fetch effect) and the hidden LightBox form fields.
    //   - leaving Ashley Advantage → drop the accepted financing term.
    //   - leaving Acima → drop the lease-approved flag + any lease error.
    // The server-side instrument is cleared separately by clearBasketPaymentInstruments at submit.
    const previousPaymentSelectionRef = useRef<string>('');
    useEffect(() => {
        const previous = previousPaymentSelectionRef.current;
        if (previous === paymentRadioValue) return;
        previousPaymentSelectionRef.current = paymentRadioValue;
        // Skip the initial bootstrap render (no real prior tender to reset).
        if (!previous) return;

        if (previous === 'new') {
            setConvergeTokenCaptured(false);
            setCapturedCard(null);
            setConvergeSession(null);
            setConvergeSessionError(null);
            // Returning to Credit Card later stays button-only (no auto-open).
            setReopenLightBox(false);
            form.setValue('isLightBoxPayment', false);
            form.setValue('sslToken', '');
            form.setValue('convergeCardType', '');
            form.setValue('convergeCardHolder', '');
            form.setValue('convergeExpirationMonth', undefined);
            form.setValue('convergeExpirationYear', undefined);
            form.setValue('convergeMaskedNumber', '');
        } else if (previous === 'ashley-advantage') {
            setAshleyAdvantageTerms(null);
        } else if (previous === 'acima') {
            setAcimaLeaseApproved(false);
            setAcimaLeaseError(null);
        }
    }, [paymentRadioValue, form]);

    const handleFormSubmit = async (data: PaymentData) => {
        const effectiveSelection = selectedPaymentMethod || paymentRadioValue;
        // Acima lease-to-own — non-card payment method. Skip CC + saved-method semantics
        // and tag the submission with paymentMethodId so the action attaches an
        // ACIMA_FINANCE payment instrument. Iframe is triggered at the place-order step.
        if (effectiveSelection === 'acima') {
            onSubmit({
                ...data,
                paymentMethodId: PAYMENT_METHOD_IDS.ACIMA_FINANCE,
                useSavedPaymentMethod: false,
                selectedSavedPaymentMethod: undefined,
            });
            return;
        }

        if (effectiveSelection === 'paypal') {
            if (!basketHasPayPal) {
                setPaypalInlineError('Please connect PayPal before continuing.');
                return;
            }
            if (data.billingSameAsShipping === false) {
                const billingValid = await form.trigger([
                    'billingFirstName',
                    'billingLastName',
                    'billingAddress1',
                    'billingCity',
                    'billingStateCode',
                    'billingPostalCode',
                    'billingCountryCode',
                ]);
                if (!billingValid) {
                    setPaypalInlineError('Please complete your billing address before continuing.');
                    return;
                }
                setPaypalInlineError(null);
                // Keep PayPal as the tender, but submit payment so the different billing address
                // is persisted to basket/profile before advancing.
                onSubmit({
                    ...data,
                    paymentType: 'paypal',
                    paymentMethodId: PAYMENT_METHOD_IDS.PAYPAL,
                    useSavedPaymentMethod: false,
                    selectedSavedPaymentMethod: undefined,
                });
                return;
            }
            setPaypalInlineError(null);
            // PayPal instrument is already persisted by /action/paypal-set-payment. Advancing
            // through submit-payment would route into credit-card validation on /checkout.data.
            checkout?.goToStep(checkout.STEPS.PLACE_ORDER);
            void revalidator.revalidate();
            return;
        }

        // Ashley Advantage — bypass credit card form; submit financing term data
        if (effectiveSelection === 'ashley-advantage') {
            // No financing offer accepted yet: surface a clear, actionable error on the AA
            // panel instead of falling through to the credit-card branch below — that would
            // submit empty hidden card fields and the server would (correctly, but
            // confusingly) reject with "Please enter your card number." etc.
            if (!ashleyAdvantageTerms) {
                form.setError('ashleyTermCode', {
                    type: 'manual',
                    message: 'Please select a financing offer to continue.',
                });
                return;
            }
            onSubmit({
                ...data,
                paymentType: 'ashley-advantage',
                ashleyTermCode: ashleyAdvantageTerms.termCode,
                ashleyApgCode: ashleyAdvantageTerms.apgCode,
                ashleyAccountNumber: ashleyAdvantageTerms.accountNumber,
                // Security code (CVV) captured in the AA panel; required on the authorize call when
                // enableSynchronyCVV is on. Empty string when the feature is off.
                ashleyCvv: ashleyAdvantageTerms.securityCode,
                // Synchrony promo disclosure HTML captured at term acceptance — forwarded to the
                // server-side authorize call so it can be stamped onto the order XML without a
                // second (sometimes-null) round-trip to Synchrony.
                ashleyDisclosureText: ashleyAdvantageTerms.disclosureText,
                ashleyCardType: ashleyCardProviderLabel,
                useSavedPaymentMethod: false,
                selectedSavedPaymentMethod: undefined,
            });
            return;
        }

        const isUsingSaved = savedPaymentMethods.some((m) => m.id === effectiveSelection);
        const paymentData = {
            ...data,
            selectedSavedPaymentMethod: isUsingSaved ? effectiveSelection : undefined,
            useSavedPaymentMethod: isUsingSaved,
        };

        onSubmit(paymentData);
    };

    // Watch billingSameAsShipping for reactive UI updates
    const billingSameAsShipping = form.watch('billingSameAsShipping');

    const [selectedBillingAddressId, setSelectedBillingAddressId] = useState('');
    const [billingDropdownOpen, setBillingDropdownOpen] = useState(false);

    const setBillingFields = (values: Record<string, string>) => {
        for (const [key, value] of Object.entries(values)) {
            form.setValue(key as keyof PaymentData, value, { shouldDirty: false, shouldValidate: false });
        }
    };

    const clearBillingFields = () => {
        setBillingFields({
            billingFirstName: '',
            billingLastName: '',
            billingAddress1: '',
            billingAddress2: '',
            billingCity: '',
            billingStateCode: '',
            billingPostalCode: '',
            billingPhone: '',
            billingCountryCode: 'US',
        });
    };

    const handleBillingAddressChange = (addressId: string) => {
        setSelectedBillingAddressId(addressId);
        if (addressId === 'new') {
            clearBillingFields();
            return;
        }
        const addr = savedAddresses.find((a) => a.id === addressId);
        if (!addr) return;
        setBillingFields({
            billingFirstName: addr.firstName ?? '',
            billingLastName: addr.lastName ?? '',
            billingAddress1: addr.address1 ?? '',
            billingAddress2: addr.address2 ?? '',
            billingCity: addr.city ?? '',
            billingStateCode: addr.stateCode ?? '',
            billingPostalCode: addr.postalCode ?? '',
            billingPhone: addr.phone ?? '',
            billingCountryCode: addr.countryCode ?? 'US',
        });
    };

    // Watch credit card and billing fields so we can clear inline errors when the user enters values
    const cardNumber = form.watch('cardNumber');
    const cardholderName = form.watch('cardholderName');
    const expiryDate = form.watch('expiryDate');
    const cvv = form.watch('cvv');
    const billingFirstName = form.watch('billingFirstName');
    const billingLastName = form.watch('billingLastName');
    const billingAddress1 = form.watch('billingAddress1');
    const billingCity = form.watch('billingCity');
    const billingPostalCode = form.watch('billingPostalCode');
    const billingStateCode = form.watch('billingStateCode');
    const billingCountryCode = form.watch('billingCountryCode');
    // Clear inline error only for the field that changed (avoids clearing all 11 on every keystroke).
    useEffect(() => {
        if ((cardNumber ?? '').trim().length > 0) form.clearErrors('cardNumber');
    }, [cardNumber, form]);
    useEffect(() => {
        if ((cardholderName ?? '').trim().length > 0) form.clearErrors('cardholderName');
    }, [cardholderName, form]);
    useEffect(() => {
        if ((expiryDate ?? '').trim().length > 0) form.clearErrors('expiryDate');
    }, [expiryDate, form]);
    useEffect(() => {
        if ((cvv ?? '').trim().length > 0) form.clearErrors('cvv');
    }, [cvv, form]);
    useEffect(() => {
        if ((billingFirstName ?? '').trim().length > 0) form.clearErrors('billingFirstName');
    }, [billingFirstName, form]);
    useEffect(() => {
        if ((billingLastName ?? '').trim().length > 0) form.clearErrors('billingLastName');
    }, [billingLastName, form]);
    useEffect(() => {
        if ((billingAddress1 ?? '').trim().length > 0) form.clearErrors('billingAddress1');
    }, [billingAddress1, form]);
    useEffect(() => {
        if ((billingCity ?? '').trim().length > 0) form.clearErrors('billingCity');
    }, [billingCity, form]);
    useEffect(() => {
        if ((billingPostalCode ?? '').trim().length > 0) form.clearErrors('billingPostalCode');
    }, [billingPostalCode, form]);
    useEffect(() => {
        if ((billingStateCode ?? '').trim().length > 0) form.clearErrors('billingStateCode');
    }, [billingStateCode, form]);
    useEffect(() => {
        if ((billingCountryCode ?? '').trim().length > 0) form.clearErrors('billingCountryCode');
    }, [billingCountryCode, form]);

    // Expose current form data to parent (single ref avoids race with place-order flow)
    const savedPaymentMethodsRef = useRef(savedPaymentMethods);
    savedPaymentMethodsRef.current = savedPaymentMethods;
    const ashleyAdvantageTermsRef = useRef(ashleyAdvantageTerms);
    ashleyAdvantageTermsRef.current = ashleyAdvantageTerms;
    useEffect(() => {
        if (!paymentSubmissionRef) return;
        const refCurrent = paymentSubmissionRef.current;
        refCurrent.formDataGetter = () => {
            const current = selectedPaymentMethodRef.current;
            const methods = savedPaymentMethodsRef.current;
            const effective =
                current ||
                (methods.length > 0 ? (methods.find((m) => m.preferred)?.id ?? methods[0]?.id ?? 'new') : 'new');
            if (effective === 'acima') {
                return {
                    ...form.getValues(),
                    paymentMethodId: PAYMENT_METHOD_IDS.ACIMA_FINANCE,
                    useSavedPaymentMethod: false,
                    selectedSavedPaymentMethod: undefined,
                };
            }
            // SSF-443: positively identify the PayPal selection so downstream
            // `doesPaymentSelectionDiffer` can distinguish "shopper kept PayPal" (no re-submit,
            // place directly against the PayPal instrument) from "shopper switched from PayPal
            // to a card/other tender" (re-submit so `clearBasketPaymentInstruments` removes the
            // PayPal instrument first). Without this signal the returned data looks like a card
            // submission and the PayPal → CC switch is silently dropped at Place Order.
            const isPayPal = effective === 'paypal';
            const isUsingSaved = !isPayPal && methods.some((m) => m.id === effective);
            const ashleyTerms = ashleyAdvantageTermsRef.current;
            return {
                ...form.getValues(),
                selectedSavedPaymentMethod: isUsingSaved ? effective : undefined,
                useSavedPaymentMethod: isUsingSaved,
                ...(isPayPal ? { paymentType: 'paypal' as const } : {}),
                // Tag Ashley Advantage whenever it is the selected method — even before a term is
                // accepted (ashleyTermCode stays empty). This makes the payment schema skip the
                // credit-card validations (the CC form is hidden for this method) and lets the
                // place-order handler require an accepted term instead of failing silently on the
                // empty hidden card fields.
                ...(effective === 'ashley-advantage'
                    ? {
                          paymentType: 'ashley-advantage' as const,
                          ashleyTermCode: ashleyTerms?.termCode ?? '',
                          ashleyApgCode: ashleyTerms?.apgCode ?? '',
                          ashleyAccountNumber: ashleyTerms?.accountNumber ?? '',
                          ashleyCvv: ashleyTerms?.securityCode ?? '',
                          ashleyDisclosureText: ashleyTerms?.disclosureText ?? '',
                          ashleyCardType: detectFinanceCardType(ashleyTerms?.accountNumber ?? ''),
                      }
                    : {}),
            };
        };
        // NOTE: the un-masked Ashley Advantage account number is threaded to place-order in
        // checkout-form-page's handlePlaceOrderSubmit (from the fresh paymentData at click time),
        // not here — this effect runs at mount before any term is accepted.
        refCurrent.setFormErrors = (errors) => {
            for (const [field, error] of Object.entries(errors)) {
                form.setError(field as keyof PaymentData, error);
            }
        };
        return () => {
            refCurrent.formDataGetter = null;
            refCurrent.setFormErrors = null;
        };
    }, [form, paymentSubmissionRef]);

    // Sync server field errors into the form so they show inline (red border + message below each field)
    useEffect(() => {
        if (!actionData?.fieldErrors || typeof actionData.fieldErrors !== 'object') return;
        for (const [field, error] of Object.entries(actionData.fieldErrors)) {
            const message = Array.isArray(error) ? error[0] : String(error);
            if (message) form.setError(field as keyof PaymentData, { type: 'server', message });
        }
    }, [actionData?.fieldErrors, form]);

    // For single page layout, always show the component but in collapsed state when not editing
    // The ToggleCard will handle the collapsed/expanded state based on editing prop

    const stepTitle = <>{t('payment.title')}</>;

    /*
     * Acima "FPP + restricted ZIP" blocked content — legacy parity for
     * `handyFppRestrictionsInStatesWithAcimaPayment`. Renders in place of the
     * Sign Lease iframe when the shopper has a Furniture Protection Plan in
     * their basket AND the shipping ZIP falls in a restricted 3-digit prefix
     * range. The shopper must remove the FPP or pick another payment method.
     * There is no CTA here — the shopper takes action elsewhere.
     */
    const acimaFppBlockedContent = (
        <div className="space-y-1" data-testid="acima-fpp-zip-blocked">
            <p className="text-sm font-medium leading-5 text-foreground">{t('payment.acima.fppBlocked.title')}</p>
            <p className="text-xs text-muted-foreground">{t('payment.acima.fppBlocked.description')}</p>
        </div>
    );

    /*
     * Acima expanded content (title, short-application copy, Apply/Sign-Lease button,
     * approval + error states). Rendered inside the same bordered box as the Acima
     * radio — below a `border-t` divider when selected — mirroring the Ashley
     * Advantage option's expand-in-place behavior.
     */
    const acimaExpandedContent = (
        <div className="space-y-2">
            <p className="text-sm font-medium leading-5 text-foreground">
                {t('payment.acima.selectedTitle', {
                    defaultValue: 'Lease-to-Own — $1 to start with Acima',
                })}
            </p>
            <p className="text-xs text-muted-foreground">
                {t('payment.acima.selectedDescription', {
                    defaultValue: 'Complete a short Acima application below before placing your order.',
                })}
            </p>
            {acimaConfig?.enabled && !acimaLeaseApproved && (
                <AcimaCheckoutButton
                    merchantId={acimaConfig.merchantId}
                    jsFileUrl={acimaConfig.jsFileUrl}
                    iframeUrl={acimaConfig.iframeUrl}
                    className="mt-2 inline-flex h-11 items-center justify-center bg-primary px-6 text-sm font-medium text-primary-foreground disabled:opacity-50"
                    label={t('payment.acima.signLease', {
                        defaultValue: 'Apply with Acima — $1 to start',
                    })}
                    requireTermsAgreement
                    termsLabel={
                        <>
                            {t('payment.acima.termsPrompt', {
                                defaultValue: 'Sign Lease button will activate when you agree to Ashley Home Furniture',
                            })}{' '}
                            <Link
                                to="/terms-and-conditions"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:text-foreground">
                                {t('payment.acima.termsLink', {
                                    defaultValue: 'Terms and Conditions',
                                })}
                            </Link>
                            .
                        </>
                    }
                    onLeaseApproved={() => {
                        setAcimaLeaseError(null);
                        setAcimaLeaseApproved(true);
                    }}
                    onError={(message) => setAcimaLeaseError(message)}
                />
            )}
            {acimaLeaseApproved && (
                <p className="mt-2 flex items-center gap-1 text-sm font-medium text-foreground">
                    <Check className="size-4" aria-hidden />
                    {t('payment.acima.leaseApproved', {
                        defaultValue: 'Acima lease approved. You can place your order.',
                    })}
                </p>
            )}
            {acimaLeaseError && (
                <p className="mt-2 text-sm text-destructive" role="alert">
                    {acimaLeaseError}
                </p>
            )}
        </div>
    );

    const handlePayPalApproved = useCallback(
        (order: PayPalApprovedOrder) => {
            const body = new FormData();
            body.set('orderId', order.id);
            body.set('fundingSource', order.fundingSource);
            void fetch('/action/paypal-set-payment', { method: 'POST', body })
                .then(
                    (res) =>
                        res.json() as Promise<{
                            success: boolean;
                            error?: string;
                            basket?: ShopperBasketsV2.schemas['Basket'];
                        }>
                )
                .then((result) => {
                    if (!result.success) {
                        setPaypalInlineError(
                            result.error ?? 'Could not attach PayPal to your order. Please try again.'
                        );
                        return;
                    }
                    setPaypalInlineError(null);
                    // Publish the PayPal-attached basket to `BasketProvider` directly. Historically
                    // this component relied on `revalidator.revalidate()` → the checkout route's
                    // `useLayoutEffect` → `updateBasket(loaderBasket)` to propagate the new PI to
                    // provider state. That path now short-circuits on `basketId`-unchanged
                    // revalidations (SSF-548-adjacent THD basket-revert fix — the loader-basket
                    // seed is intentionally guarded to keep it from clobbering fetcher-owned
                    // writes). Without a direct push here, PayPal would stay stuck in
                    // "select payment method" — no `basketHasPayPal`, no Continue-to-Review CTA.
                    // Every other tender (CC / Acima / Ashley Advantage) already publishes inline
                    // via its own fetcher effect in `use-checkout-actions.ts:283-289`; PayPal was
                    // the odd one out because `/action/paypal-set-payment` runs from a plain
                    // `fetch` (not a `useFetcher`), so the provider never saw the response.
                    if (result.basket) {
                        updateBasket(result.basket);
                    }
                    // Pin the shopper on the Payment step so they can review the connected PayPal
                    // account and click "Continue" — matching the flow for Credit Card / Acima /
                    // Ashley Advantage. Attaching PayPal makes hasCompletedPayment() true, which
                    // advances computeStepFromBasket() to PLACE_ORDER; for returning customers
                    // (editingStep=null) CheckoutProvider's basket-driven auto-advance effect
                    // would then collapse the Payment card and jump straight to Order Review the
                    // moment revalidate() resolves. goToStep(PAYMENT) sets editingStep=PAYMENT,
                    // which short-circuits that effect and keeps the section open until the
                    // shopper submits the payment form (handlePaymentSubmit then calls
                    // exitEditMode() to advance). Applies regardless of billing-same-as-shipping
                    // so all payment methods have consistent "click Continue" UX (client request,
                    // 2026-07-16).
                    checkout?.goToStep(checkout.STEPS.PAYMENT);
                    void revalidator.revalidate();
                })
                .catch(() => setPaypalInlineError('Could not attach PayPal to your order. Please try again.'));
        },
        [checkout, revalidator, updateBasket]
    );

    const paypalExpandedContent = (
        <div className="space-y-3">
            {paypalSdkConfig ? (
                <PayPalButton
                    config={paypalSdkConfig}
                    variant="checkout"
                    showPayLater={showPayLaterButton}
                    onApproved={handlePayPalApproved}
                    onError={(message) => setPaypalInlineError(message)}
                    onCancel={() => setPaypalInlineError(null)}
                />
            ) : null}
            {basketHasPayPal && (
                <p className="text-sm font-medium text-foreground">
                    PayPal connected. Click Continue below to review your order.
                </p>
            )}
            {paypalInlineError && (
                <p className="text-sm text-destructive" role="alert">
                    {paypalInlineError}
                </p>
            )}
        </div>
    );

    return (
        <div ref={paymentSectionRef}>
            <ToggleCard
                id="payment"
                title={stepTitle as React.ReactNode}
                editing={isEditing}
                disabled={isUpcomingStep ? false : disabled}
                disableEdit={isUpcomingStep}
                onEdit={onEdit}
                editLabel={t('payment.changeLabel')}
                isLoading={isLoading}>
                <ToggleCardEdit>
                    <Form {...form}>
                        <form onSubmit={(e) => void form.handleSubmit(handleFormSubmit)(e)} className="space-y-6">
                            {/* CHECKOUT-17 — Kount Device Data Collector is mounted ONCE at the
                                checkout layout root (see <CheckoutFormPage/> inside
                                <KountReadyProvider/>). It cannot live inside this ToggleCardEdit
                                subtree: the top-of-checkout Express PayPal button
                                (<CheckoutExpressPayments/>) advances step → PLACE_ORDER on approve
                                in well under the DDC's 5-second collect window, which would unmount
                                the collector, clear its safety timeout, and leave kountReady stuck
                                false — blocking the Place Order CTA for the entire PayPal flow.
                                Mounted at the layout root, the collector lives for the lifetime of
                                checkout and the timeout always fires. (The in-step PayPal path
                                inside this ToggleCard now pins to PAYMENT on approve — see
                                handlePayPalApproved — so it no longer participates in that race,
                                but the express path still requires the root-mounted DDC.) */}
                            {paymentFormError && <CheckoutErrorBanner message={paymentFormError} />}

                            {/* Payment Method Section */}
                            <div className="space-y-0">
                                <UITarget targetId="checkout.payment.paymentMethods.before" />
                                <UITarget targetId="checkout.payment.paymentMethods">
                                    <>
                                        {!showAshleyTermsOnly && (
                                            <>
                                                {/* Saved Payment Methods + Credit Card, with View All (n more) when > 3 options */}
                                                {savedPaymentMethods.length > 0 && (
                                                    <div className="space-y-3">
                                                        <RadioGroup
                                                            value={paymentRadioValue}
                                                            onValueChange={handlePaymentMethodSelectionChange}
                                                            className="space-y-0">
                                                            {visiblePaymentOptionIds.map((optionId) => {
                                                                if (optionId === 'new') {
                                                                    if (
                                                                        paymentRadioValue === 'new' &&
                                                                        savedPaymentMethods.length > 0
                                                                    ) {
                                                                        return null;
                                                                    }
                                                                    return (
                                                                        <div
                                                                            key="new"
                                                                            className={`flex items-center gap-2 border border-input p-4 ${
                                                                                paymentRadioValue === 'new'
                                                                                    ? 'bg-secondary'
                                                                                    : 'bg-background'
                                                                            }`}>
                                                                            <RadioGroupItem
                                                                                value="new"
                                                                                id="new-payment"
                                                                            />
                                                                            <Label
                                                                                htmlFor="new-payment"
                                                                                className="flex-1 cursor-pointer flex items-center gap-2">
                                                                                <span className="text-sm font-medium leading-5 text-foreground">
                                                                                    {t('payment.creditCardOption')}
                                                                                </span>
                                                                                <CreditCardBrandLogos />
                                                                            </Label>
                                                                        </div>
                                                                    );
                                                                }
                                                                if (optionId === 'acima') {
                                                                    return (
                                                                        <div
                                                                            key="acima"
                                                                            className={`border border-input ${
                                                                                paymentRadioValue === 'acima'
                                                                                    ? 'bg-secondary'
                                                                                    : 'bg-background'
                                                                            }`}>
                                                                            <div className="flex items-center gap-2 p-4">
                                                                                <RadioGroupItem
                                                                                    value="acima"
                                                                                    id="acima-payment"
                                                                                />
                                                                                <Label
                                                                                    htmlFor="acima-payment"
                                                                                    className="flex flex-1 cursor-pointer items-center gap-2">
                                                                                    <span className="flex-1">
                                                                                        <span className="block text-sm font-medium leading-5 text-foreground">
                                                                                            {t(
                                                                                                'payment.acima.optionTitle',
                                                                                                {
                                                                                                    defaultValue:
                                                                                                        'Lease-to-Own with Acima — $1 to start',
                                                                                                }
                                                                                            )}
                                                                                        </span>
                                                                                        <span className="block text-xs text-muted-foreground mt-0.5">
                                                                                            {t(
                                                                                                'payment.acima.optionDescription',
                                                                                                {
                                                                                                    defaultValue:
                                                                                                        'No credit needed. Apply at the next step.',
                                                                                                }
                                                                                            )}
                                                                                        </span>
                                                                                    </span>
                                                                                    <AcimaWordmark />
                                                                                </Label>
                                                                            </div>
                                                                            {paymentRadioValue === 'acima' && (
                                                                                <div className="border-t border-input px-4 pb-4 pt-4">
                                                                                    {acimaExpandedContent}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }
                                                                if (optionId === 'paypal') {
                                                                    return (
                                                                        <div
                                                                            key="paypal"
                                                                            className={`border border-input ${
                                                                                paymentRadioValue === 'paypal'
                                                                                    ? 'bg-secondary'
                                                                                    : 'bg-background'
                                                                            }`}>
                                                                            <div className="flex items-center gap-2 p-4">
                                                                                <RadioGroupItem
                                                                                    value="paypal"
                                                                                    id="paypal-payment"
                                                                                />
                                                                                <Label
                                                                                    htmlFor="paypal-payment"
                                                                                    className="flex flex-1 cursor-pointer items-center gap-2">
                                                                                    <span className="flex-1">
                                                                                        <span className="block text-sm font-medium leading-5 text-foreground">
                                                                                            {t(
                                                                                                'payment.paypal.optionTitle',
                                                                                                {
                                                                                                    defaultValue:
                                                                                                        'PayPal',
                                                                                                }
                                                                                            )}
                                                                                        </span>
                                                                                        <span className="block text-xs text-muted-foreground mt-0.5">
                                                                                            {t(
                                                                                                'payment.paypal.optionDescription',
                                                                                                {
                                                                                                    defaultValue:
                                                                                                        'Connect your PayPal account to pay securely.',
                                                                                                }
                                                                                            )}
                                                                                        </span>
                                                                                    </span>
                                                                                    <PayPalBrandIcon />
                                                                                </Label>
                                                                            </div>
                                                                            {paymentRadioValue === 'paypal' && (
                                                                                <div className="border-t border-input px-4 pb-4 pt-4">
                                                                                    {paypalExpandedContent}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }
                                                                const method = savedPaymentMethods.find(
                                                                    (m) => m.id === optionId
                                                                );
                                                                if (!method) return null;
                                                                const cardTypeIdentifier = method.cardType || 'unknown';
                                                                const CardIcon = getCardIcon(cardTypeIdentifier);
                                                                const cardTypeLabel = method.cardType
                                                                    ? method.cardType.charAt(0).toUpperCase() +
                                                                      method.cardType.slice(1).toLowerCase()
                                                                    : t('payment.unknownCardType');
                                                                return (
                                                                    <div
                                                                        key={method.id}
                                                                        className={`flex items-start gap-2 border border-input p-4 ${
                                                                            paymentRadioValue === method.id
                                                                                ? 'bg-secondary'
                                                                                : 'bg-background'
                                                                        }`}>
                                                                        <RadioGroupItem
                                                                            value={method.id}
                                                                            id={method.id}
                                                                            className="mt-0.5"
                                                                        />
                                                                        <Label
                                                                            htmlFor={method.id}
                                                                            className="flex-1 cursor-pointer min-w-0">
                                                                            <div className="flex flex-col gap-1">
                                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                                    <span className="text-sm font-medium leading-5 text-foreground">
                                                                                        {cardTypeLabel}
                                                                                    </span>
                                                                                    {method.preferred && (
                                                                                        <Badge
                                                                                            variant="secondary"
                                                                                            className="text-xs font-normal bg-primary/10 text-primary rounded-none">
                                                                                            {t('payment.defaultBadge')}
                                                                                        </Badge>
                                                                                    )}
                                                                                </div>
                                                                                <span className="text-sm font-normal leading-5 text-muted-foreground">
                                                                                    {t('payment.endingIn', {
                                                                                        lastDigits: getLastFourDigits(
                                                                                            method.maskedNumber
                                                                                        ),
                                                                                    })}
                                                                                </span>
                                                                            </div>
                                                                        </Label>
                                                                        <CardIcon
                                                                            className="w-6 h-4 flex-shrink-0 text-muted-foreground mt-0.5"
                                                                            aria-hidden
                                                                        />
                                                                    </div>
                                                                );
                                                            })}
                                                        </RadioGroup>
                                                        {hiddenPaymentCount > 0 ? (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-sm font-medium text-foreground"
                                                                onClick={() => setShowAllPaymentOptions(true)}
                                                                aria-expanded={false}>
                                                                {t('payment.viewAllMore', {
                                                                    count: hiddenPaymentCount,
                                                                })}
                                                            </Button>
                                                        ) : (
                                                            allPaymentOptionIds.length > INITIAL_VISIBLE_COUNT &&
                                                            paymentRadioValue !== 'new' && (
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-sm font-medium text-foreground"
                                                                    onClick={handleViewLess}
                                                                    aria-expanded={true}>
                                                                    {t('payment.viewLess')}
                                                                </Button>
                                                            )
                                                        )}
                                                    </div>
                                                )}

                                                {/* Credit Card compact option (guest checkout, when another method is selected).
                                        Mirrors the Acima/Ashley Advantage compact radios so the Credit Card choice stays
                                        selectable — otherwise selecting financing would hide it with no way to switch back. */}
                                                {savedPaymentMethods.length === 0 && paymentRadioValue !== 'new' && (
                                                    <div
                                                        data-payment-option
                                                        className={`flex items-center gap-2 border border-input p-4 ${
                                                            paymentRadioValue === 'new'
                                                                ? 'bg-secondary'
                                                                : 'bg-background'
                                                        }`}>
                                                        <RadioGroup
                                                            value=""
                                                            className="flex items-center gap-2 flex-1"
                                                            onValueChange={() => {
                                                                userHasChosenPaymentMethodRef.current = true;
                                                                setSelectedPaymentMethod('new');
                                                            }}>
                                                            <RadioGroupItem
                                                                value="new"
                                                                id="credit-card-option-compact"
                                                            />
                                                            <Label
                                                                htmlFor="credit-card-option-compact"
                                                                className="flex items-center gap-2 cursor-pointer flex-1">
                                                                <span className="text-sm font-medium leading-5 text-foreground">
                                                                    {t('payment.creditCardOption')}
                                                                </span>
                                                                <CreditCardBrandLogos />
                                                            </Label>
                                                        </RadioGroup>
                                                    </div>
                                                )}

                                                {/* Credit Card form — shown only when "new" (Credit Card) is the selected method. */}
                                                {paymentRadioValue === 'new' && (
                                                    <div className="space-y-0">
                                                        <div className="space-y-4 border border-input bg-secondary p-4">
                                                            {(savedPaymentMethods.length === 0 ||
                                                                paymentRadioValue === 'new') && (
                                                                <div className="flex items-center gap-2">
                                                                    <RadioGroup
                                                                        value="new"
                                                                        className="flex items-center gap-2 flex-1"
                                                                        onValueChange={() => {
                                                                            userHasChosenPaymentMethodRef.current = true;
                                                                            setSelectedPaymentMethod('new');
                                                                        }}>
                                                                        <RadioGroupItem
                                                                            value="new"
                                                                            id="credit-card-option"
                                                                        />
                                                                        <Label
                                                                            htmlFor="credit-card-option"
                                                                            className="flex items-center gap-2 cursor-pointer flex-1">
                                                                            <span className="text-sm font-medium leading-5 text-foreground">
                                                                                {t('payment.creditCardOption')}
                                                                            </span>
                                                                            <CreditCardBrandLogos />
                                                                        </Label>
                                                                    </RadioGroup>
                                                                </div>
                                                            )}

                                                            {convergeEnabled ? (
                                                                <div className="space-y-2">
                                                                    {convergeSessionError && (
                                                                        <p className="text-sm text-destructive">
                                                                            {convergeSessionError}
                                                                        </p>
                                                                    )}
                                                                    {convergeTokenCaptured ? (
                                                                        <div className="space-y-2">
                                                                            <div className="space-y-0.5">
                                                                                <p className="text-sm font-normal leading-5 text-foreground">
                                                                                    {`${capturedCard?.cardType ?? ''} **** ${capturedCard?.lastFour ?? ''}`.trim()}
                                                                                </p>
                                                                                {capturedCard?.expirationMonth &&
                                                                                    capturedCard?.expirationYear && (
                                                                                        <p className="text-sm font-normal leading-5 text-foreground">
                                                                                            {t(
                                                                                                'payment.expiresSummary',
                                                                                                {
                                                                                                    expiry: `${String(
                                                                                                        capturedCard.expirationMonth
                                                                                                    ).padStart(
                                                                                                        2,
                                                                                                        '0'
                                                                                                    )}/${capturedCard.expirationYear}`,
                                                                                                }
                                                                                            )}
                                                                                        </p>
                                                                                    )}
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                onClick={handleReenterCard}
                                                                                className="text-sm font-medium text-primary underline underline-offset-4 hover:no-underline">
                                                                                {t('payment.useDifferentCard', {
                                                                                    defaultValue:
                                                                                        'Use a different card',
                                                                                })}
                                                                            </button>
                                                                        </div>
                                                                    ) : convergeSession ? (
                                                                        <ConvergeLightBox
                                                                            sessionToken={convergeSession.token}
                                                                            firstName={convergeSession.firstName}
                                                                            lastName={convergeSession.lastName}
                                                                            address={convergeSession.address}
                                                                            postalCode={convergeSession.postalCode}
                                                                            txnId={convergeSession.txnId}
                                                                            totalAmount={convergeSession.totalAmount}
                                                                            convergeCheckoutUrl={
                                                                                convergePrefs?.checkoutUrl ?? ''
                                                                            }
                                                                            cardTypeMappings={
                                                                                convergePrefs?.cardTypeMappings ?? {}
                                                                            }
                                                                            onPaymentToken={handleConvergeToken}
                                                                            onError={(msg) =>
                                                                                setConvergeSessionError(msg)
                                                                            }
                                                                            onClose={loadConvergeSession}
                                                                            autoOpen={reopenLightBox}
                                                                            buttonLabel={t('payment.enterCardDetails', {
                                                                                defaultValue: 'Enter Card Details',
                                                                            })}
                                                                            className="w-full border border-input bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                                                                        />
                                                                    ) : (
                                                                        <button
                                                                            type="button"
                                                                            disabled
                                                                            aria-busy="true"
                                                                            className="w-full border border-input bg-primary text-primary-foreground px-4 py-2 text-sm font-medium disabled:opacity-50">
                                                                            {t('payment.enterCardDetails', {
                                                                                defaultValue: 'Enter Card Details',
                                                                            })}
                                                                        </button>
                                                                    )}
                                                                    <input
                                                                        type="hidden"
                                                                        {...form.register('isLightBoxPayment')}
                                                                        value="true"
                                                                    />
                                                                    <input
                                                                        type="hidden"
                                                                        {...form.register('sslToken')}
                                                                    />
                                                                    <input
                                                                        type="hidden"
                                                                        {...form.register('convergeCardType')}
                                                                    />
                                                                    <input
                                                                        type="hidden"
                                                                        {...form.register('convergeCardHolder')}
                                                                    />
                                                                    <input
                                                                        type="hidden"
                                                                        {...form.register('convergeExpirationMonth')}
                                                                    />
                                                                    <input
                                                                        type="hidden"
                                                                        {...form.register('convergeExpirationYear')}
                                                                    />
                                                                    <input
                                                                        type="hidden"
                                                                        {...form.register('convergeMaskedNumber')}
                                                                    />
                                                                    <input
                                                                        type="hidden"
                                                                        {...form.register('convergeSessionToken')}
                                                                    />
                                                                    <input
                                                                        type="hidden"
                                                                        {...form.register('convergeTransactionInfo')}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <CreditCardInputFields
                                                                    form={form}
                                                                    autoFocus={isEditing && paymentRadioValue === 'new'}
                                                                />
                                                            )}
                                                            {customerProfile?.customer?.customerId &&
                                                            !hidePaymentSaveCheckbox ? (
                                                                <FormField
                                                                    control={form.control}
                                                                    name="savePaymentToProfile"
                                                                    render={({ field }) => {
                                                                        return (
                                                                            <FormItem className="space-y-0">
                                                                                <label
                                                                                    htmlFor={field.name}
                                                                                    className="flex cursor-pointer items-start gap-3">
                                                                                    <FormControl>
                                                                                        <Checkbox
                                                                                            id={field.name}
                                                                                            checked={
                                                                                                field.value ?? false
                                                                                            }
                                                                                            onCheckedChange={(
                                                                                                checked
                                                                                            ) => {
                                                                                                field.onChange(
                                                                                                    checked === true
                                                                                                );
                                                                                            }}
                                                                                            className="size-5 shrink-0 rounded border-2 border-input data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground"
                                                                                            aria-label={t(
                                                                                                'payment.savePaymentToProfile'
                                                                                            )}
                                                                                        />
                                                                                    </FormControl>
                                                                                    <span className="text-sm font-normal leading-none text-foreground pt-0.5">
                                                                                        {t(
                                                                                            'payment.savePaymentToProfile'
                                                                                        )}
                                                                                    </span>
                                                                                </label>
                                                                            </FormItem>
                                                                        );
                                                                    }}
                                                                />
                                                            ) : null}
                                                        </div>
                                                        {showViewLessUnderForm && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-sm font-medium text-foreground"
                                                                onClick={handleViewLess}
                                                                aria-expanded={true}>
                                                                {t('payment.viewLess')}
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Ashley Advantage® financing card option (INT-48) */}
                                        <div
                                            data-payment-option
                                            className={`-mt-px space-y-0 ${
                                                !showAshleyTermsOnly && paymentRadioValue === 'ashley-advantage'
                                                    ? 'bg-secondary'
                                                    : ''
                                            }`}>
                                            <div
                                                className={`${
                                                    showAshleyTermsOnly
                                                        ? ''
                                                        : `border border-input ${
                                                              paymentRadioValue === 'ashley-advantage'
                                                                  ? 'bg-muted/10'
                                                                  : 'bg-background'
                                                          }`
                                                }`}>
                                                {!showAshleyTermsOnly && (
                                                    <div className="flex items-center gap-2 p-4">
                                                        <RadioGroup
                                                            value={
                                                                paymentRadioValue === 'ashley-advantage'
                                                                    ? 'ashley-advantage'
                                                                    : '__none__'
                                                            }
                                                            onValueChange={() => {
                                                                userHasChosenPaymentMethodRef.current = true;
                                                                setSelectedPaymentMethod('ashley-advantage');
                                                            }}
                                                            className="flex items-center gap-2 flex-1">
                                                            <RadioGroupItem
                                                                value="ashley-advantage"
                                                                id="ashley-advantage-option"
                                                            />
                                                            <Label
                                                                htmlFor="ashley-advantage-option"
                                                                className="flex flex-1 cursor-pointer items-center gap-2">
                                                                <span className="text-sm font-medium leading-5 text-foreground">
                                                                    {ta('payment.ashleyAdvantageOption')}
                                                                </span>
                                                                <AshleyAdvantageWordmark />
                                                            </Label>
                                                        </RadioGroup>
                                                    </div>
                                                )}
                                                {paymentRadioValue === 'ashley-advantage' && (
                                                    <div
                                                        className={
                                                            showAshleyTermsOnly
                                                                ? ''
                                                                : 'px-4 pb-4 border-t border-input pt-4'
                                                        }>
                                                        {!showAshleyTermsOnly &&
                                                            form.formState.errors.ashleyTermCode && (
                                                                <p
                                                                    className="mb-3 text-sm text-status-error"
                                                                    role="alert">
                                                                    {String(
                                                                        form.formState.errors.ashleyTermCode.message ??
                                                                            ''
                                                                    )}
                                                                </p>
                                                            )}
                                                        {ashleyAdvantageTerms ? (
                                                            <div className="space-y-2">
                                                                <p className="text-sm text-foreground font-medium">
                                                                    {`${ashleyCardProviderLabel} **** ${ashleyAccountLastFour}`}
                                                                </p>
                                                                <p className="text-sm text-foreground font-medium">
                                                                    {ta('ashleyAdvantage.selectedSummary', {
                                                                        description: ashleyAdvantageTerms.description,
                                                                    })}
                                                                </p>
                                                                <p className="text-sm text-foreground font-medium">
                                                                    {`Financed: ${ashleyFinancedAmount} · $0 down`}
                                                                </p>
                                                                <button
                                                                    type="button"
                                                                    className="text-xs underline text-muted-foreground"
                                                                    onClick={() => setAshleyAdvantageTerms(null)}>
                                                                    {t('payment.changeLabel')}
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <AshleyAdvantagePayment
                                                                ref={ashleyAdvantageRef}
                                                                basketTotal={shopperVisibleTotal}
                                                                initialAccountNumber={lastAshleyAccountNumber}
                                                                cvvEnabled={synchronyCvvEnabled}
                                                                termsAgreementContentAsset={
                                                                    synchronyTermsAgreementContentAsset
                                                                }
                                                                onTermsScreenChange={setShowAshleyTermsOnly}
                                                                onTermSelected={(term) => {
                                                                    setAshleyAdvantageTerms(term);
                                                                    setLastAshleyAccountNumber(term.accountNumber);
                                                                    setShowAshleyTermsOnly(false);
                                                                    form.clearErrors('ashleyTermCode');
                                                                }}
                                                            />
                                                        )}
                                                        {!showAshleyTermsOnly && !ashleyAdvantageTerms && (
                                                            <div className="mt-2">
                                                                <Button
                                                                    type="button"
                                                                    size="lg"
                                                                    className="h-14 w-full"
                                                                    onClick={() =>
                                                                        ashleyAdvantageRef.current?.viewOffers()
                                                                    }>
                                                                    {t('payment.viewFinancingOptions')}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {!showAshleyTermsOnly && savedPaymentMethods.length === 0 && (
                                            <>
                                                {/* Acima is shown after Ashley Advantage for Figma order parity. */}
                                                {isAcimaEligible && (
                                                    <div
                                                        className={`border border-input ${
                                                            paymentRadioValue === 'acima'
                                                                ? 'bg-secondary'
                                                                : 'bg-background'
                                                        }`}>
                                                        <div className="flex items-center gap-2 p-4">
                                                            <RadioGroup
                                                                value={paymentRadioValue === 'acima' ? 'acima' : ''}
                                                                className="flex items-center gap-2 flex-1"
                                                                onValueChange={() => {
                                                                    userHasChosenPaymentMethodRef.current = true;
                                                                    setSelectedPaymentMethod('acima');
                                                                }}>
                                                                <RadioGroupItem value="acima" id="acima-payment-only" />
                                                                <Label
                                                                    htmlFor="acima-payment-only"
                                                                    className="flex flex-1 cursor-pointer items-center gap-2">
                                                                    <span className="flex-1">
                                                                        <span className="block text-sm font-medium leading-5 text-foreground">
                                                                            {t('payment.acima.optionTitle', {
                                                                                defaultValue:
                                                                                    'Lease-to-Own with Acima — $1 to start',
                                                                            })}
                                                                        </span>
                                                                        <span className="block text-xs text-muted-foreground mt-0.5">
                                                                            {t('payment.acima.optionDescription', {
                                                                                defaultValue:
                                                                                    'No credit needed. Apply at the next step.',
                                                                            })}
                                                                        </span>
                                                                    </span>
                                                                    <AcimaWordmark />
                                                                </Label>
                                                            </RadioGroup>
                                                        </div>
                                                        {paymentRadioValue === 'acima' && (
                                                            <div className="border-t border-input px-4 pb-4 pt-4">
                                                                {isAcimaFppZipBlocked
                                                                    ? acimaFppBlockedContent
                                                                    : acimaExpandedContent}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {paypalSdkConfig && (
                                                    <div
                                                        className={`border border-input ${
                                                            paymentRadioValue === 'paypal'
                                                                ? 'bg-secondary'
                                                                : 'bg-background'
                                                        }`}>
                                                        <div className="flex items-center gap-2 p-4">
                                                            <RadioGroup
                                                                value={paymentRadioValue === 'paypal' ? 'paypal' : ''}
                                                                className="flex items-center gap-2 flex-1"
                                                                onValueChange={() => {
                                                                    userHasChosenPaymentMethodRef.current = true;
                                                                    setSelectedPaymentMethod('paypal');
                                                                }}>
                                                                <RadioGroupItem
                                                                    value="paypal"
                                                                    id="paypal-payment-only"
                                                                />
                                                                <Label
                                                                    htmlFor="paypal-payment-only"
                                                                    className="flex flex-1 cursor-pointer items-center gap-2">
                                                                    <span className="flex-1">
                                                                        <span className="block text-sm font-medium leading-5 text-foreground">
                                                                            {t('payment.paypal.optionTitle', {
                                                                                defaultValue: 'PayPal',
                                                                            })}
                                                                        </span>
                                                                        <span className="block text-xs text-muted-foreground mt-0.5">
                                                                            {t('payment.paypal.optionDescription', {
                                                                                defaultValue:
                                                                                    'Connect your PayPal account to pay securely.',
                                                                            })}
                                                                        </span>
                                                                    </span>
                                                                    <PayPalBrandIcon />
                                                                </Label>
                                                            </RadioGroup>
                                                        </div>
                                                        {paymentRadioValue === 'paypal' && (
                                                            <div className="border-t border-input px-4 pb-4 pt-4">
                                                                {paypalExpandedContent}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                </UITarget>
                                <UITarget targetId="checkout.payment.paymentMethods.after" />
                            </div>
                            {/* Billing Address Section */}
                            {!showAshleyTermsOnly && (
                                <div className="space-y-4">
                                    <UITarget targetId="checkout.payment.billingAddress.before" />
                                    <UITarget targetId="checkout.payment.billingAddress">
                                        <div className="border-t border-input pt-4 space-y-4">
                                            {showBillingSameAsShipping && (
                                                <FormField
                                                    control={form.control}
                                                    name="billingSameAsShipping"
                                                    render={({ field }) => (
                                                        <FormItem className="space-y-0">
                                                            <label
                                                                htmlFor={field.name}
                                                                className="flex cursor-pointer items-start gap-3">
                                                                <FormControl>
                                                                    <Checkbox
                                                                        id={field.name}
                                                                        checked={!field.value}
                                                                        onCheckedChange={(checked) => {
                                                                            field.onChange(checked !== true);
                                                                        }}
                                                                        aria-label={t('payment.useDifferentBilling')}
                                                                    />
                                                                </FormControl>
                                                                <span className="text-sm font-normal leading-none text-foreground pt-0.5">
                                                                    {t('payment.useDifferentBilling')}
                                                                </span>
                                                            </label>
                                                        </FormItem>
                                                    )}
                                                />
                                            )}

                                            {!billingSameAsShipping && (
                                                <div className="space-y-4">
                                                    {billingAddressOptions.length > 0 && (
                                                        <Popover
                                                            open={billingDropdownOpen}
                                                            onOpenChange={setBillingDropdownOpen}>
                                                            <PopoverTrigger asChild>
                                                                <button
                                                                    type="button"
                                                                    className="flex w-full items-center justify-between border border-input bg-card px-4 h-9 text-sm font-medium text-foreground">
                                                                    <span
                                                                        className={`truncate ${!selectedBillingAddressId ? 'text-muted-foreground' : ''}`}>
                                                                        {!selectedBillingAddressId
                                                                            ? t('payment.selectAnAddress')
                                                                            : selectedBillingAddressId === 'new'
                                                                              ? `+ ${t('shippingAddress.addNewAddressButton')}`
                                                                              : formatAddress(
                                                                                    billingAddressOptions.find(
                                                                                        (a) =>
                                                                                            a.id ===
                                                                                            selectedBillingAddressId
                                                                                    )
                                                                                ).fullAddress}
                                                                    </span>
                                                                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
                                                                </button>
                                                            </PopoverTrigger>
                                                            <PopoverContent
                                                                align="start"
                                                                sideOffset={4}
                                                                aria-label={t('payment.selectAnAddress')}
                                                                className="w-[var(--radix-popover-trigger-width)] rounded-none border border-input bg-card p-0 shadow-md">
                                                                <div className="max-h-[108px] overflow-y-auto">
                                                                    {[...billingAddressOptions]
                                                                        .sort((a, b) => {
                                                                            const sel = selectedBillingAddressId;
                                                                            if (a.id === sel) return -1;
                                                                            if (b.id === sel) return 1;
                                                                            return 0;
                                                                        })
                                                                        .map((address) => {
                                                                            const isSelected =
                                                                                selectedBillingAddressId === address.id;
                                                                            return (
                                                                                <button
                                                                                    key={address.id}
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        handleBillingAddressChange(
                                                                                            address.id
                                                                                        );
                                                                                        setBillingDropdownOpen(false);
                                                                                    }}
                                                                                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent">
                                                                                    <span className="flex-1 truncate text-left">
                                                                                        {
                                                                                            formatAddress(address)
                                                                                                .fullAddress
                                                                                        }
                                                                                    </span>
                                                                                    {isSelected && (
                                                                                        <Check className="h-4 w-4 shrink-0" />
                                                                                    )}
                                                                                </button>
                                                                            );
                                                                        })}
                                                                </div>
                                                                <div className="sticky bottom-0 border-t border-input bg-card">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            handleBillingAddressChange('new');
                                                                            setBillingDropdownOpen(false);
                                                                        }}
                                                                        className="flex w-full items-center px-3 py-2 text-sm text-foreground hover:bg-accent">
                                                                        {`+ ${t('shippingAddress.addNewAddressButton')}`}
                                                                    </button>
                                                                </div>
                                                            </PopoverContent>
                                                        </Popover>
                                                    )}
                                                    {(selectedBillingAddressId === 'new' ||
                                                        billingAddressOptions.length === 0) && (
                                                        <div className="bg-muted p-4">
                                                            <AddressFormFields
                                                                form={form}
                                                                fieldPrefix="billing"
                                                                showPhone={false}
                                                                showCountry
                                                                countryCode="US"
                                                                autoFocus
                                                                autoFocusField="firstName"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </UITarget>
                                    <UITarget targetId="checkout.payment.billingAddress.after" />
                                </div>
                            )}

                            {/* CHECKOUT-14 — submit payment and advance to the order-review step.
                                OOTB folded payment submission into the single Place Order CTA; Ashley
                                restores a discrete payment step (legacy OPC parity) so the shopper
                                confirms payment, then reviews before placing the order.
                                Gated on isPaymentReadyToAuth so the CTA only surfaces once the selected
                                tender has a payment instrument ready to authorize (card captured /
                                saved card selected / lease approved / financing term accepted). */}
                            {!showAshleyTermsOnly && isPaymentReadyToAuth && (
                                <div>
                                    <Button
                                        type="submit"
                                        size="lg"
                                        disabled={isLoading}
                                        className="h-auto whitespace-normal w-full lg:w-fit"
                                        data-testid="payment-continue-button">
                                        {isLoading ? t('payment.saving') : t('payment.continue')}
                                    </Button>
                                </div>
                            )}
                        </form>
                    </Form>
                </ToggleCardEdit>

                <ToggleCardSummary>
                    {isUpcomingStep ? (
                        <p className="text-sm text-muted-foreground">{t('shippingOptions.completePreviousSteps')}</p>
                    ) : (
                        <div className="space-y-0.5 w-full">
                            {hasAshleyAdvantageSummary ? (
                                <>
                                    <p className="text-sm font-normal leading-5 text-foreground">
                                        {`${ashleyCardProviderLabel} **** ${ashleyAccountLastFour}`}
                                    </p>
                                    <p className="text-sm font-normal leading-5 text-foreground">
                                        {ashleyAdvantageTerms?.description}
                                    </p>
                                    <p className="text-sm font-normal leading-5 text-foreground">
                                        {`Financed: ${ashleyFinancedAmount} · $0 down`}
                                    </p>
                                </>
                            ) : hasSummaryPaymentMethod ? (
                                <>
                                    <p className="text-sm font-normal leading-5 text-foreground">
                                        {paymentMethod?.paymentMethodId === PAYMENT_METHOD_IDS.ASHLEY_FINANCE
                                            ? `${summaryFinanceLabel} **** ${summaryFinanceLastFour || '****'}`
                                            : `${summaryMethodLabel} **** ${summaryLastFour}`}
                                    </p>
                                    {hasSummaryExpiry && (
                                        <p className="text-sm font-normal leading-5 text-foreground">
                                            {t('payment.expiresSummary', {
                                                expiry: `${summaryExpiryMonth}/${summaryExpiryYear}`,
                                            })}
                                        </p>
                                    )}
                                    {!billingAddress || isBillingSameAsShipping(billingAddress, shippingAddress) ? (
                                        <p className="text-sm font-normal leading-5 text-foreground">
                                            {t('payment.billingSummaryPrefix', {
                                                value: t('payment.sameAsShippingAddress'),
                                            })}
                                        </p>
                                    ) : (
                                        <div className="text-sm font-normal leading-5 text-foreground">
                                            <p>
                                                {t('payment.billingSummaryPrefix', {
                                                    value: formatAddress(billingAddress).nameLine,
                                                })}
                                            </p>
                                            <p>{formatAddress(billingAddress).streetLine}</p>
                                            <p>{formatAddress(billingAddress).cityLine}</p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="text-sm font-normal leading-5 text-muted-foreground">
                                    {t('payment.noPaymentMethodSaved')}
                                </p>
                            )}
                        </div>
                    )}
                </ToggleCardSummary>
            </ToggleCard>
        </div>
    );
}
